import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  Box,
  ButtonGroup,
  Card,
  CardHeader,
  IconButton,
  Stack
} from '@mui/material';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Grid from '@mui/system/Unstable_Grid';

import PostCommentList from 'src/layouts/common/threads/post-comment-list';
import { useParams, useRouter } from 'src/routes/hooks';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { useTranslate } from 'src/locales';
import { PageOptions } from 'src/services/api.types';
import { useDeleteMeetingMutation, useGetMeetingByIdQuery } from 'src/services/meetings/meetings.service';

import { LoadingButton } from '@mui/lab';
import { enqueueSnackbar } from 'notistack';
import { ConfirmDialog } from 'src/components/custom-dialog';
import Iconify from 'src/components/iconify';
import TruncatedTextField from 'src/components/truncate-text/truncated-text-field';
import { useBoolean } from 'src/hooks/use-boolean';
import PostCommentForm from 'src/layouts/common/threads/post-comment-form';
import { paths } from 'src/routes/paths';
import { LinkedContactsCard } from 'src/sections/contacts/components/linked-contacts-card';
import { MeetingStatusLabel } from 'src/sections/meetings/components/meeting-status-label';
import MeetingEditorModal from 'src/sections/meetings/modal/meeting-editor-modal';
import MeetingInfoSection from 'src/sections/meetings/sections/meeting-info-section';
import RecordingSection from 'src/sections/meetings/sections/recording-section';
import { ContactLinkEntityType } from 'src/services/contacts/contacts.types';

// ----------------------------------------------------------------------

const DEFAULT_PAGE: PageOptions = {
  page: 1,
  per_page: 10,
};

enum MeetingTab {
  RECORDING = 'recording',
  DETAILS = 'details'
}

export default function NotetakerDetailView() {

  const { t } = useTranslate();

  const { meeting_id } = useParams();

  const meetingEditorControl = useBoolean();

  const confirmDelete = useBoolean();

  const [currentTab, setCurrentTab] = useState<MeetingTab | undefined>();

  const [searchParams, setSearchParams] = useSearchParams();

  const [deleteMeeting, { isLoading: isDeletingMeeting }] = useDeleteMeetingMutation();

  const router = useRouter();

  const tenantType = useOrgTenant();

  useEffect(() => {
    // eslint-disable-next-line no-useless-return
    if (currentTab) return;

    let defaultTab: MeetingTab = MeetingTab.RECORDING;

    const existingTab = searchParams.get('tab');
    if (existingTab && Object.values(MeetingTab).includes(existingTab as MeetingTab)) {
      defaultTab = existingTab as MeetingTab;
    }

    setCurrentTab(defaultTab);

  }, [currentTab, searchParams]);

  const { currentData: meeting } = useGetMeetingByIdQuery(meeting_id as string, {
    skip: !meeting_id,
  });

  const handleChangeTab = useCallback((_event: React.SyntheticEvent, newValue: MeetingTab) => {
    setCurrentTab(newValue);
  }, []);

  useEffect(() => {
    // eslint-disable-next-line no-useless-return
    if (!currentTab) return;

    setSearchParams({ "tab": currentTab }, { replace: true });

  }, [currentTab, setSearchParams]);

  const renderHead = (
    <Card sx={{ flexGrow: 0, boxShadow: 'unset', pb: 1 }}>
      <CardHeader
        sx={{ pl: 0, width: '100%' }}
        title={
          <Stack direction="column" justifyContent="start" alignItems="center">
            <TruncatedTextField
              text={meeting ? meeting.title : "Loading..."}
              limit={40}
              mode='characters'
              variant="h3"
              sx={{ mb: 0, lineHeight: 1 }}
              showMoreEnabled={false}
            />
            <TruncatedTextField
              text={meeting ? meeting.description : "Loading..."}
              limit={40}
              mode='characters'
              variant="subtitle2"
              sx={{ color: 'text.secondary' }}
              showMoreEnabled={false}
            />
          </Stack>
        }
        action={
          <Stack direction="row" spacing={.5} alignItems="center" mt={2}>
            {meeting && (
              <MeetingStatusLabel status={meeting.status} />
            )}
          </Stack>
        }
      />
    </Card>
  );

  const renderTabs = (
    <Tabs value={currentTab} onChange={handleChangeTab} sx={{ pl: 2, mb: 2 }}>
      <Tab
        key={MeetingTab.RECORDING}
        iconPosition="end"
        value={MeetingTab.RECORDING}
        label={t('meetings.detail.tabs.recording')}
      />

      <Tab
        key={MeetingTab.DETAILS}
        iconPosition="end"
        value={MeetingTab.DETAILS}
        label={t('meetings.detail.tabs.details')}
      />
    </Tabs>
  );

  const onDelete = useCallback(async () => {
    if (!meeting) return;

    try {
      await deleteMeeting(meeting.id).unwrap();

      confirmDelete.onFalse();
      enqueueSnackbar(t('meetings.api.delete.success'), { variant: 'success' });

      router.push(paths.dashboard.notetaker.root);
    } catch (e) {
      enqueueSnackbar(t('meetings.api.delete.default_error'), { variant: 'error' });
    }
  }, [deleteMeeting, meeting, confirmDelete, router, t]);

  return (
    <>
      <Grid container>
        <Grid xs={12}>
          {renderHead}
        </Grid>
      </Grid>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center" justifyContent="space-between">
        {renderTabs}

        <Stack direction="row" spacing={1} alignItems="center">

          <ButtonGroup>
            <IconButton
              color='primary'
              onClick={() => meetingEditorControl.onTrue()}
            >
              <Iconify icon="material-symbols:edit" />
            </IconButton>
            <IconButton
              color='error'
              onClick={() => confirmDelete.onTrue()}
            >
              <Iconify icon="material-symbols:delete" />
            </IconButton>
          </ButtonGroup>

        </Stack>
      </Stack>

      {currentTab === MeetingTab.RECORDING && (
        <Grid container spacing={3}>
          <Grid xs={12} md={8}>
            <RecordingSection meeting={meeting} />
          </Grid>
          <Grid xs={12} md={4}>
            <Stack direction="column" spacing={2}>
              <LinkedContactsCard entityId={meeting?.id as string} entityType={ContactLinkEntityType.MEETING} enableTypeSelection={false}/>

              <Card>
                <CardHeader title={t('common.notes')} />
                <Box sx={{ p: 3 }}>
                  <Box>
                    <PostCommentForm thread_id={meeting?.thread?.id} />
                    <PostCommentList thread_id={meeting?.thread?.id} />
                  </Box>
                </Box>
              </Card>

            </Stack>
          </Grid>
        </Grid>
      )}

      {currentTab === MeetingTab.DETAILS && (
        <Grid container spacing={3}>
          <Grid xs={12} md={8}>
            <MeetingInfoSection meeting={meeting} />
          </Grid>
          <Grid xs={12} md={4}>
            <Stack direction="column" spacing={2}>
              <LinkedContactsCard entityId={meeting?.id as string} entityType={ContactLinkEntityType.MEETING} enableTypeSelection={false} />

              <Card>
                <CardHeader title={t('common.notes')} />
                <Box sx={{ p: 3 }}>
                  <Box>
                    <PostCommentForm thread_id={meeting?.thread?.id} />
                    <PostCommentList thread_id={meeting?.thread?.id} />
                  </Box>
                </Box>
              </Card>

            </Stack>
          </Grid>
        </Grid>
      )}

      {
        (meeting && meetingEditorControl.value) && (
          <MeetingEditorModal open={meetingEditorControl.value} onClose={meetingEditorControl.onFalse} meetingId={meeting.id} />
        )
      }

      <ConfirmDialog
        open={confirmDelete.value}
        onClose={confirmDelete.onFalse}
        title={t('meetings.delete_confirm.title')}
        content={t('meetings.delete_confirm.content')}
        action={
          <LoadingButton variant="contained" color="error" onClick={onDelete} loading={isDeletingMeeting}>
            {t('common.delete')}
          </LoadingButton>
        }
      />

    </>
  );
}